


































































































import { Component, Mixins, Watch } from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import TagsMixin from "@/mixins/tagsMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import leadSorters from "@/data/sorters/lead";
import leadFilters from "@/data/filters/lead";

// Types
import { IFilter, ISorter } from "@/models/tables";
import { IState } from "@/store";
import { ITag } from "@/models/tags";
import { ILead } from "@/models/leads";

@Component({
  components: {
    "lead-u-ripple-row": () =>
      import("@/components/app/admin/leads/leadURippleRow.vue")
  },
  computed: {
    ...mapState({
      listParams(state: IState, getters) {
        return getters[`data/${this.dataModule}/listParams`]();
      }
    }),
    ...mapGetters({
      isMultibrand: "brand/isMultibrand"
    })
  }
})
export default class LeadsTable extends Mixins(
  FilteringSortingTableStore,
  TagsMixin
) {
  public isMultibrand!: object;
  public listParams!: object;

  public canDeleteLead = this.$userCan("delete_lead");
  public canViewLead = this.$userCan("get_lead");
  public canUpdateLead = this.$userCan("update_lead");
  public guardFunctionalities = ["list_leads"];

  public sorters: ISorter[] = leadSorters;
  public filters: IFilter[] = leadFilters;

  public dataModule: string = DataModules.LEADS;
  public isLoading = true;

  public async created() {
    this.loadData();
  }

  public async loadData() {
    try {
      this.isLoading = true;
      await this.getTags();
      this.apiProps = {
        with: `related_leads,tags${this.isMultibrand ? ",brand" : ""}`
      };
      this.setFilters();
      this.getData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.loading = false;
    }
  }

  public applyTagFilter(tag: ITag) {
    let tagFilter = this.$_.find(this.activeFilters, ["key", "tags.id"]);
    if (!tagFilter) {
      tagFilter = this.$_.cloneDeep(
        this.createTagFilter("tags.id", this.$t("_.tag"), "")
      );
      this.activeFilters.push(tagFilter);
    }
    this.$set(tagFilter, "value", [tag.id]);
  }

  public createTicket(lead) {
    this.$store.dispatch("data/tickets/openNewTicketModal", {
      leadId: this.$_.get(lead, "id"),
      brandId: this.$_.get(lead, "brand_id")
    });
  }

  @Watch("$route.query")
  public setFilters() {
    this.filters = this.$_.uniqBy(
      this.$_.concat(leadFilters, [
        this.createTagFilter("tags.id", this.$t("_.tag"), "")
      ]),
      "key"
    );
    if (!this.hasIsHiddenFilter) {
      this.filters = this.filters.filter(f => f.key !== "is_hidden");
    }
  }

  public get hasIsHiddenFilter() {
    return (
      !!this.$route.query[this.id] &&
      this.$route.query[this.id].includes("$is_hidden")
    );
  }

  public get filterForIsHidden() {
    return this.hasIsHiddenFilter ? {} : { "filter[is_hidden]": 0 };
  }

  public async confirmDeleteLead(leadId: ILead["id"], relationCount: number) {
    if (!this.canDeleteLead) {
      return;
    }
    let message = "delete_confirmation";
    if (relationCount) {
      message = "delete_lead_with_relations";
    }
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$tc(`_sentence.${message}`, relationCount, {
            object: (this.$t("_.lead") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.deleteLead(leadId);
            confirm.close();
          }
        }
      }
    });
  }

  public async deleteLead(leadId: ILead["id"]) {
    this.table.loading = true;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/remove`, {
        id: leadId
      });
      await this.refreshList();

      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_removed", {
          object: this.$t("_.lead")
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.table.loading = false;
  }

  public async updateHidden(lead: ILead) {
    try {
      if (!lead) return;
      const is_hidden = !lead.is_hidden;
      await this.$store.dispatch(`data/${this.dataModule}/update`, {
        id: lead.id,
        data: { brand_id: lead.brand_id, is_hidden }
      });
      await this.getData({ ignoreStored: true });
      this.$buefy.toast.open({
        message: this.$t(
          is_hidden
            ? "_.lead_hidden_successfully"
            : "_.lead_unhidden_successfully"
        ) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public refreshList() {
    return this.$store.dispatch(`data/${this.dataModule}/list`, {
      params: this.listParams,
      scope: this.dataScope,
      ignoreStored: true,
      vm: this
    });
  }

  // overrides mixin method
  public getStoreParams(payload: any): Promise<any> {
    return {
      ...payload,
      params: {
        "filter[is_main]": "1",
        "filter[assigned_client_at]": "null",
        ...payload.params,
        ...this.filterForIsHidden
      }
    };
  }
}
